import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import { parsePaginatedList } from '../collection-helpers';
import { Trash2, BookOpen } from 'lucide-react';
import Pagination from '@/components/ui/Pagination';
import ColecaoObraCard from './ColecaoObraCard';

interface Obra {
    id: number;
    nome: string;
    imagem?: string;
    formato?: { id: number; nome: string; };
    total_capitulos: number;
    total_lidos?: number;
    ultimo_cap_numero?: string;
    ultimo_lido?: any;
    [key: string]: any;
}

interface StatusTabProps {
    statusId: number;
    statusNome: string;
    currentPage?: number;
    itemsPerPage?: number;
}

export default function StatusTab({ statusId, statusNome, currentPage = 1, itemsPerPage = 20 }: StatusTabProps) {
    const { isAuthenticated, user } = useAuth();
    const [searchParams] = useSearchParams();

    const formatoId = useMemo(() => {
        const f = searchParams.get('formato');
        if (f) {
            const n = Number(f);
            if (!Number.isNaN(n) && Number.isInteger(n) && n > 0) return n;
        }
        return 5;
    }, [searchParams]);

    const page = useMemo(() => {
        const p = Number(currentPage);
        return Number.isFinite(p) && p >= 1 ? Math.floor(p) : 1;
    }, [currentPage]);

    const [works, setWorks] = useState<Obra[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const fetchWorksByStatus = async (pagina: number) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limite: itemsPerPage.toString(),
                pagina: String(pagina),
                ordenarPor: 'criada_em_desc',
                formato: String(formatoId),
            });
            const res = await api.get(`readings/by-status/${statusId}?${params.toString()}`);
            const { items, totalPages, totalItems } = parsePaginatedList<Obra>(res);
            setWorks(items);
            setTotalItems(totalItems);
            setTotalPages(totalPages);
        } catch (error) { setWorks([]); } finally { setLoading(false); }
    };

    useEffect(() => { fetchWorksByStatus(page); }, [statusId, page, formatoId, itemsPerPage]);

    const handleRemoveWork = async (e: React.MouseEvent, workId: number) => {
        e.preventDefault(); e.stopPropagation();
        if (!isAuthenticated || !user?.usr_id) return;
        if (!window.confirm('Deseja realmente remover esta obra da sua leitura e histórico?')) return;
        try {
            await api.delete(`readings/${workId}`);
            setWorks((prev) => prev.filter(w => w.id !== workId));
            setTotalItems((prev) => Math.max(0, prev - 1));
        } catch (error) { alert('Não foi possível remover a obra.'); }
    };

    if (loading && works.length === 0) return <div className="flex flex-col items-center py-16 text-white"><div className="w-10 h-10 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mb-4" /><p className="text-gray-300">Carregando {statusNome}...</p></div>;
    if (works.length === 0) return (
        <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-white/10 bg-white/[0.03]">
            <BookOpen className="w-20 h-20 text-white/20 mb-4" />
            <p className="text-white/50 text-lg text-center px-4">{`Nenhuma obra em "${statusNome}" neste formato.`}</p>
        </div>
    );

    return (
        <div className="animate-in fade-in duration-500">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mb-8">
                {works.map((work) => (
                    <div key={work.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-2 hover:border-orange-500/25 transition-colors">
                        <ColecaoObraCard
                            context="status"
                            work={work}
                            overlayStart={
                                <button
                                    type="button"
                                    onClick={(e) => handleRemoveWork(e, work.id)}
                                    className="p-2 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-white/60 hover:text-red-400 hover:bg-black/70 transition-all md:opacity-0 md:group-hover:opacity-100 shadow-md"
                                    title="Remover do histórico"
                                >
                                    <Trash2 size={16} />
                                </button>
                            }
                            overlayEnd={
                                work.formato?.nome ? (
                                    <span className="px-2 py-0.5 bg-black/55 backdrop-blur-md rounded text-[10px] font-semibold uppercase tracking-wider text-white/90 border border-white/10">
                                        {work.formato.nome}
                                    </span>
                                ) : null
                            }
                        />
                    </div>
                ))}
            </div>
            <Pagination
                currentPage={page}
                totalPages={totalPages}
                baseUrl="/colecao"
                extraParams={{ aba: statusNome.toLowerCase(), formato: formatoId }}
            />
        </div>
    );
}
